import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkle, FileText, Download, Palette, Tag, Timer } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { getPaymentConfig } from "@/lib/payment";
import { getLocalCurrency, formatLocalPrice } from "@/lib/localCurrency";

export default function Landing() {
  const [discount, setDiscount] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [localCurrency, setLocalCurrency] = useState(null);

  useEffect(() => {
    getPaymentConfig()
      .then((cfg) => setDiscount(cfg?.discount || null))
      .catch(() => {});
    // Local currency is best-effort; if the user is US-based or detection
    // fails we simply render USD only.
    getLocalCurrency().then(setLocalCurrency).catch(() => {});
  }, []);

  // Tick every second so the countdown updates live. Only runs while a
  // discount with an end date is active — cleaned up otherwise.
  useEffect(() => {
    if (!discount?.ends_at) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [discount?.ends_at]);

  // Format the time remaining as "Xd Yh Zm Ws", collapsing units as the
  // window shrinks. Returns null when the promo has ended so the caller can
  // suppress the bar entirely.
  const countdown = (() => {
    if (!discount?.ends_at) return null;
    const endMs = new Date(discount.ends_at).getTime();
    if (Number.isNaN(endMs)) return null;
    const remaining = endMs - now;
    if (remaining <= 0) return { expired: true, label: "Ended" };
    const s = Math.floor(remaining / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    let label;
    if (d > 0) label = `${d}d ${h}h ${m}m`;
    else if (h > 0) label = `${h}h ${m}m ${sec}s`;
    else if (m > 0) label = `${m}m ${sec}s`;
    else label = `${sec}s`;
    return { expired: false, label };
  })();

  // Hide the whole bar once the promo has expired client-side (backend will
  // also stop returning it once _get_active_discount's window check fails).
  const showBar = discount && !(countdown && countdown.expired);

  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans">
      {showBar && (
        <div
          className="w-full bg-[#FF2A00] text-white text-xs sm:text-sm px-4 py-2 flex items-center justify-center gap-3 flex-wrap"
          data-testid="landing-discount-bar"
        >
          <Tag size={14} weight="fill" />
          <span className="font-mono font-bold uppercase tracking-widest">
            {discount.percent}% OFF
          </span>
          <span className="opacity-90">{discount.label}</span>
          <span className="opacity-90">·</span>
          <span>
            <span className="line-through opacity-60 mr-1">USD {discount.original_usd}</span>
            <b>USD {Number(discount.final_usd).toFixed(2)}</b>
            {localCurrency && (
              <span
                className="ml-1 opacity-80 text-[11px] sm:text-xs"
                data-testid="landing-discount-local-price"
              >
                (~{formatLocalPrice(Number(discount.final_usd), localCurrency)} {localCurrency.currency})
              </span>
            )}
          </span>
          {countdown && !countdown.expired && (
            <>
              <span className="opacity-90">·</span>
              <span
                className="inline-flex items-center gap-1.5 bg-black/25 rounded-sm px-2 py-0.5 font-mono tabular-nums"
                data-testid="landing-discount-countdown"
                aria-label={`Ends in ${countdown.label}`}
              >
                <Timer size={13} weight="fill" />
                <span>Ends in <b>{countdown.label}</b></span>
              </span>
            </>
          )}
          <Link
            to="/get-started"
            className="underline hover:no-underline font-medium ml-2"
            data-testid="landing-discount-cta"
          >
            Claim now →
          </Link>
        </div>
      )}
      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5"
        data-testid="landing-header"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <Logo />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-700">
            <a href="#features" className="hover:text-black transition-colors" data-testid="nav-features">Features</a>
            <a href="#templates" className="hover:text-black transition-colors" data-testid="nav-templates">Templates</a>
            <a href="#how" className="hover:text-black transition-colors" data-testid="nav-how">How it works</a>
            <Link to="/about" className="hover:text-black transition-colors" data-testid="nav-about">About</Link>
            <Link to="/contact" className="hover:text-black transition-colors" data-testid="nav-contact">Contact</Link>
            <Link to="/help" className="hover:text-black transition-colors" data-testid="nav-help">Help</Link>
          </nav>
          <Link
            to="/get-started"
            className="btn-primary text-sm"
            data-testid="header-cta-build"
          >
            Start building — Free
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </header>

      {/* Hero — asymmetric Tetris grid */}
      <section className="relative overflow-hidden border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            <div className="col-span-12 lg:col-span-8 animate-fade-up">
              <div className="label-mono mb-6">
                <span className="inline-block w-6 h-px bg-black/40 align-middle mr-2" />
                Resume Builder / v1.0
              </div>
              <h1 className="font-heading font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-8xl">
                Write a résumé
                <br />
                that <span className="shimmer-text">gets you hired.</span>
              </h1>
              <p className="mt-8 max-w-xl text-base lg:text-lg text-neutral-600 leading-relaxed">
                MyResumeDesign turns raw career notes into a sharp, recruiter-ready document. Nine
                editorial templates including ATS-safe and photo-sidebar layouts, live preview,
                AI polish, resume import, and JD-matched tailoring.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link to="/get-started" className="btn-primary" data-testid="hero-cta-build">
                  Build my résumé — Free
                  <ArrowRight size={16} weight="bold" />
                </Link>
                <a href="#templates" className="btn-secondary" data-testid="hero-cta-templates">
                  Browse templates
                </a>
              </div>
              <div className="mt-12 flex items-center gap-8 label-mono">
                <span>No login required</span>
                <span className="text-black/20">/</span>
                <span>PDF in one click</span>
                <span className="text-black/20 hidden sm:inline">/</span>
                <span className="hidden sm:inline">AI-powered polish</span>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-4 relative">
              <div className="paper aspect-[3/4] relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
                <div className="absolute inset-0 p-8 flex flex-col">
                  <div className="pb-4 border-b-2 border-black">
                    <div className="font-heading font-black text-xl tracking-tight">Alex Chen</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">
                      Senior Product Designer
                    </div>
                  </div>
                  <div className="mt-4 space-y-3">
                    <div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#2400FF]">Experience</div>
                      <div className="mt-1 space-y-1">
                        <div className="h-1.5 bg-neutral-900 w-3/4 rounded-sm" />
                        <div className="h-1 bg-neutral-300 w-full rounded-sm" />
                        <div className="h-1 bg-neutral-300 w-5/6 rounded-sm" />
                        <div className="h-1 bg-neutral-300 w-2/3 rounded-sm" />
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#2400FF]">Skills</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {["Figma", "React", "Systems", "Motion"].map((s) => (
                          <span key={s} className="text-[9px] px-1.5 py-0.5 border border-black/20 rounded-sm">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-3 -right-3 bg-[#FF2A00] text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1.5">
                  Preview
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Tetris */}
      <section id="features" className="border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            <div className="col-span-12 lg:col-span-5">
              <div className="label-mono mb-4">01 / What it does</div>
              <h2 className="font-heading font-bold text-3xl lg:text-5xl leading-tight tracking-tight">
                Editorial-grade résumés,<br />built in minutes.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-7 grid grid-cols-2 gap-6">
              {[
                { icon: FileText, title: "9 templates", body: "Modern, Classic, Minimal, Creative, Executive, ATS-safe, Two-column, Photo-header — each set in a distinct type system." },
                { icon: Sparkle, title: "AI polish", body: "Rewrites bullets and summaries into recruiter-ready copy in seconds. Facts stay yours." },
                { icon: Palette, title: "Live preview", body: "Every keystroke rendered instantly on a paper-like preview surface." },
                { icon: Download, title: "1-click PDF", body: "Client-side export. Nothing leaves your browser except the file you download." },
              ].map((f) => (
                <div key={f.title} className="border border-black/10 p-6 hover:border-black/40 transition-colors">
                  <f.icon size={24} weight="duotone" className="text-[#2400FF]" />
                  <div className="mt-4 font-heading font-semibold text-lg tracking-tight">{f.title}</div>
                  <div className="mt-2 text-sm text-neutral-600 leading-relaxed">{f.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Templates */}
      <section id="templates" className="border-b border-black/5 bg-neutral-50/50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <div>
              <div className="label-mono mb-4">02 / Templates</div>
              <h2 className="font-heading font-bold text-3xl lg:text-5xl leading-tight tracking-tight">
                Five voices.<br />One story — yours.
              </h2>
            </div>
            <p className="max-w-md text-sm text-neutral-600 leading-relaxed">
              Every template is hand-tuned. Same content, wildly different personality — switch
              instantly in the builder.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
            {[
              { name: "Modern", tag: "Grotesk / Sharp" },
              { name: "Classic", tag: "Serif / Balanced" },
              { name: "Minimal", tag: "Mono / Restrained" },
              { name: "Creative", tag: "Editorial / Bold" },
              { name: "Executive", tag: "Two-column / Dense" },
            ].map((t, i) => (
              <div
                key={t.name}
                className="paper aspect-[3/4] relative animate-fade-up group cursor-default"
                style={{ animationDelay: `${i * 0.06}s` }}
              >
                <div className="absolute inset-0 p-4 flex flex-col">
                  <div className={`h-1 w-8 ${i === 3 ? "bg-[#FF2A00]" : "bg-[#2400FF]"}`} />
                  <div className="mt-3 font-heading font-bold text-sm">{t.name}</div>
                  <div className="mt-1 text-[9px] font-mono uppercase tracking-widest text-neutral-400">
                    {t.tag}
                  </div>
                  <div className="mt-4 space-y-1.5 flex-1">
                    <div className="h-1 bg-neutral-800 w-2/3" />
                    <div className="h-0.5 bg-neutral-300 w-full" />
                    <div className="h-0.5 bg-neutral-300 w-5/6" />
                    <div className="h-0.5 bg-neutral-300 w-4/6" />
                    <div className="h-2" />
                    <div className="h-1 bg-neutral-800 w-1/2" />
                    <div className="h-0.5 bg-neutral-300 w-full" />
                    <div className="h-0.5 bg-neutral-300 w-3/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            <div className="col-span-12 lg:col-span-4">
              <div className="label-mono mb-4">03 / How</div>
              <h2 className="font-heading font-bold text-3xl lg:text-5xl leading-tight tracking-tight">
                Four moves.<br />Zero fluff.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-8 space-y-0 border-t border-black/10">
              {[
                { n: "01", t: "Fill the form", b: "Personal details, work, education, skills. Sample data pre-loaded if you want to explore first." },
                { n: "02", t: "Polish with AI", b: "Click the red spark on any bullet or summary. Claude rewrites it — you approve or reject." },
                { n: "03", t: "Customise the typography", b: "Fine-tune fonts, sizes, weights, and colours for every section — from the name headline down to job titles and body copy — so the résumé matches your personal brand." },
                { n: "04", t: "Export as PDF", b: "One button, one file. High-res, print-safe, single or multi-page." },
              ].map((s) => (
                <div key={s.n} className="grid grid-cols-12 gap-6 py-8 border-b border-black/10 group hover:bg-neutral-50/60 transition-colors">
                  <div className="col-span-2 font-mono text-sm text-neutral-400">{s.n}</div>
                  <div className="col-span-10 lg:col-span-4 font-heading font-semibold text-xl tracking-tight">{s.t}</div>
                  <div className="col-span-12 lg:col-span-6 text-neutral-600 leading-relaxed">{s.b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-neutral-950 text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-8">
              <h2 className="font-heading font-black tracking-tighter text-4xl lg:text-7xl leading-[0.95]">
                Your next role<br />is one draft away.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
              <Link
                to="/get-started"
                className="inline-flex items-center gap-3 bg-white text-black px-6 py-4 text-sm font-medium rounded-sm hover:-translate-y-[1px] transition-all"
                data-testid="footer-cta-build"
              >
                Start building — free
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/10 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-neutral-500">
          <div className="font-mono">© 2026 MyResumeDesign</div>
          <div className="flex flex-wrap items-center gap-6 font-mono uppercase tracking-widest text-xs" data-testid="footer-links">
            <Link to="/about" className="hover:text-black" data-testid="footer-about">About</Link>
            <Link to="/contact" className="hover:text-black" data-testid="footer-contact">Contact</Link>
            <Link to="/help" className="hover:text-black" data-testid="footer-help">Help</Link>
            <Link to="/privacy" className="hover:text-black" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-black" data-testid="footer-terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
